import {Plural, t, Trans} from '@lingui/macro'
import Axios, {AxiosError} from 'axios'
import React from 'react'
import {Link as RouterLink} from 'react-router-dom'
import styled from 'styled-components'

import {Inline, Link, LoadingOverlay, Stack, Text, tokens} from '@pleo-io/telescope'

import {isRequestError} from '@product-web/api-error'
import {requestOtp} from '@product-web/auth--otp/api'
import {useTimer} from '@product-web/auth--otp/timer/use-timer'
import {getErrorMessage} from '@product-web/error/message'
import {reportError} from '@product-web/error/report'
import {useAppLanguage} from '@product-web/i18n'
import {useToaster} from '@product-web/toaster'
import {useDebouncePromise} from '@product-web/use-debounce-promise'
import {LoginScreen, useLoginLocationState} from '@product-web-features/sca/login/login.helpers'
import {
    ErrorMessageType,
    getTimeThrottled,
    RESET_ERROR_DELAY,
    useOtpNotifications,
} from '@product-web-features/sca/sca'

import Screen from '../../../components/auth-flow-screen-template'
import {usePasscodeInput} from '../../../components/passcode-input'
import EnterPasscode from '../../../images/enter-passcode.svg'
import {DEBOUNCE_OTP_REQUEST_MS} from '../../../libs/debounce-otp-request'
import * as s from '../../../libs/otp.styles'

export function PasscodeScreenV2() {
    const {
        state: {email},
        update,
    } = useLoginLocationState()
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [canRetry, setCanRetry] = React.useState(true)
    const [timeThrottled, setTimeThrottled] = React.useState(0)
    const [submitPinCount, setSubmitPinCount] = React.useState(0)
    const countDownAttempts = submitPinCount >= 1 && submitPinCount < 4
    const notifications = useOtpNotifications()
    const appLanguage = useAppLanguage()
    const isThrottled = timeThrottled > 0
    const {showToast} = useToaster()

    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    const navigateBack = () => {
        setIsLoading(false)
        setHasError(false)
        setCanRetry(true)
        update({
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
            screen: LoginScreen.EMAIL,
        })
    }

    const onSubmit = async (passcode: string) => {
        if (!email || !passcode) {
            return
        }
        try {
            const response = await requestOtpDebounced({email, passcode, language: appLanguage})
            update({
                ...response,
                passcode,
                screen: LoginScreen.OTP_EMAIL_VALIDATION,
            })
        } catch (err) {
            const message = getErrorMessage(err)
            if (Axios.isAxiosError(err) || err instanceof AxiosError) {
                const headerPasscodeLimit = err.response?.headers['x-pleo-passcode-limit']
                const remainingPasscodeAttempts = headerPasscodeLimit
                    ? parseInt(headerPasscodeLimit, 10)
                    : 0

                setSubmitPinCount(remainingPasscodeAttempts)
            }

            setHasError(!!message)
            if (isRequestError(err) && message === ErrorMessageType.throttled) {
                const throttled = getTimeThrottled(err.response)
                setCanRetry(false)
                setTimeThrottled(throttled)
                setTimeout(() => {
                    setHasError(false)
                    passcodeInput.reset()
                }, RESET_ERROR_DELAY) // used to remove error styling of input after 2 seconds
            } else if (message === ErrorMessageType.phone_otp_limit_exceeded) {
                notifications[ErrorMessageType.phone_otp_limit_exceeded]()
                navigateBack()
            } else if (message === ErrorMessageType.network_error) {
                showToast(t`Please try again, and if the problem continues, contact support.`, {
                    title: t`Can't submit passcode`,
                    level: 'error',
                })
                setIsLoading(false)
                setHasError(false)
                passcodeInput.reset()
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    setHasError(false)
                    update({
                        phoneLastDigits: undefined,
                        passcode: null,
                    })
                    passcodeInput.reset()
                }, RESET_ERROR_DELAY)
            }

            reportError(err)
        }
    }
    const passcodeInput = usePasscodeInput({
        onSubmit,
        hasError: hasError || isThrottled,
        alignLeft: true,
    })

    const blockedTimer = useTimer(timeThrottled)
    const accountLockedTimerHours = blockedTimer.hours === '00' ? '' : `${blockedTimer.hours}:`
    const accountLockedTimer = `${accountLockedTimerHours}${blockedTimer.minutes}:${blockedTimer.seconds}`

    React.useEffect(() => {
        if (blockedTimer.currentTime <= 0) {
            setTimeThrottled(0)
        }
    }, [blockedTimer.currentTime])

    const getValidationMessage = () => {
        if (hasError && canRetry && !countDownAttempts) {
            return (
                <Text variant="medium-default" color="red700">
                    <Trans>Incorrect passcode</Trans>
                </Text>
            )
        }

        if (hasError && canRetry && countDownAttempts) {
            return (
                <Text color="red700">
                    <Plural
                        value={submitPinCount}
                        one={`Incorrect passcode. You have # attempt left before you
                        are blocked for 5 minutes.`}
                        other={`Incorrect passcode. You have # attempts left before you
                        are blocked for 5 minutes.`}
                    />
                </Text>
            )
        }

        const throttledMessage = (
            <Text color="red700">
                <Text variant="medium-default" color="red700">
                    <Trans>
                        Too many failed attempts. Your account has been locked, try again in{' '}
                        {accountLockedTimer}.
                    </Trans>
                </Text>
            </Text>
        )

        if (isThrottled) {
            return throttledMessage
        }

        return null
    }

    return (
        <Screen
            hideImageOnSmallScreen={true}
            imgSrc={EnterPasscode}
            hideLogoOnSmallScreen={false}
            onBack={navigateBack}
        >
            <s.Form>
                <Section>
                    <Stack space={40} stretch>
                        {isLoading && <LoadingOverlay data-testid="loading-indicator" />}

                        <Stack space={10}>
                            <Text variant="3xlarge-accent" align="left">
                                <Trans>Enter passcode</Trans>
                            </Text>
                            <Pill>{email}</Pill>
                        </Stack>
                        <Stack space={16}>
                            {passcodeInput.element}
                            {getValidationMessage()}
                        </Stack>
                        <Inline alignX="left">
                            <Link as={RouterLink} to="/reset/passcode">
                                <Trans>Forgot passcode?</Trans>
                            </Link>
                        </Inline>
                    </Stack>
                </Section>
            </s.Form>
        </Screen>
    )
}

export const Pill = styled.div`
    width: fit-content;
    padding: ${tokens.spacing4} ${tokens.spacing12};
    background-color: ${tokens.shade200};
    border-radius: ${tokens.arc20};
    border: ${tokens.borderPrimary};
    font-size: ${tokens.fontMedium};
    font-weight: ${tokens.fontWeightMedium};
    color: ${tokens.shade700};
`

const Section = styled.div`
    width: 400px;
`
